/**
 * 조감도 뷰 (디자인: 전사_조감도 뷰 1·2·3 PNG) — 공장마다 카드 하나.
 *  [조감도 그림]
 *  국기 공장명 영문국가 · 공장코드                                   [정상/주의]
 *  (가동률 도넛)  설비 수 | 가동 설비 | 총 알람
 *                설비 상태 막대 (가동·대기·정지)
 *  온도 · 습도 · 미세먼지                         [Critical N] [자세히 보기 >]
 *
 * 데이터: GET /dashboard/company-factory (공장별 설비 현황·가동률·생산량·알람·최신 환경 측정값).
 *  - 가동률 = 가동 설비 / 전체 설비 (서버 실시간 판정, 마지막 샷 10분 기준).
 *  - 환경값은 factory_environment 최신 측정값이고, 측정 기록이 없으면 —.
 *  - 조감도 그림은 public/images/aerial/{공장코드}.png 가 있으면 보여 주고, 없으면 빈 칸에 공장 아이콘만 둔다.
 *    (디자인 PNG 는 그림 자리만 표시돼 있어 실제 그림 파일은 아직 없다)
 * 디자인의 가운데 칸 "가동률" 은 도넛과 같은 값이라, 같은 숫자를 두 번 보이지 않도록 가동 설비 대수로 바꿨다.
 * (응답의 production 은 가동 설비 마지막 샷카운트 합이라 "오늘 생산량" 이 아니어서 쓰지 않는다)
 */
import { memo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCompanyFactoryOverview } from "../../../services/api";
import { useAutoRefresh } from "../../../hooks/useAutoRefresh";
import { useAlarmNotification } from "../../../context/AlarmNotificationContext";
import FactoryFlag, { countryOfFactory } from "../../../components/factory/FactoryFlag";
import { formatInt, formatNumber } from "../../lib/format";

const COUNTRY_EN = { KR: "Korea", VN: "Vietnam" };
const DASH = "—";
const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

/** 공장 상태 배지: 오류·경고 알람이 있으면 주의 */
function factoryState(f) {
    const a = f.alarms || {};
    return (Number(a.error) || 0) + (Number(a.warning) || 0) > 0 ? "warn" : "good";
}

function Donut({ value, tone }) {
    const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
    const r = 62;
    const c = 2 * Math.PI * r;
    return (<div className={`dfv-donut is-${tone}`} role="img" aria-label={`가동률 ${value == null ? "미측정" : `${formatNumber(value, 1)}%`}`}>
      <svg viewBox="0 0 140 140" aria-hidden="true">
        <circle cx="70" cy="70" r={r} className="dfv-donut__track"/>
        <circle cx="70" cy="70" r={r} className="dfv-donut__bar" strokeDasharray={`${(pct / 100) * c} ${c}`} transform="rotate(-90 70 70)"/>
      </svg>
      <div className="dfv-donut__text">
        <span><b className={value != null && value >= 100 ? "is-long" : undefined}>{value == null ? DASH : formatNumber(value, 1)}</b>{value != null && <small>%</small>}</span>
        <em>가동률</em>
      </div>
    </div>);
}

function Illustration({ code, name }) {
    const [failed, setFailed] = useState(false);
    const src = code ? `${process.env.PUBLIC_URL || ""}/images/aerial/${encodeURIComponent(code)}.png` : null;
    return (<div className="dfv-aerial__art">
      {src && !failed
            ? <img src={src} alt={`${name} 조감도`} onError={() => setFailed(true)}/>
            : (<div className="dfv-aerial__art-empty" title="조감도 그림 파일이 아직 없습니다 (public/images/aerial/{공장코드}.png)">
            <span className="material-symbols-outlined" aria-hidden="true">factory</span>
            <span>{name}</span>
          </div>)}
    </div>);
}

const FactoryCard = memo(function FactoryCard({ f, onDetail, onCritical }) {
    const country = countryOfFactory(f.factoryName, f.location);
    const state = factoryState(f);
    const eq = f.equipment || {};
    const total = Number(eq.total) || 0;
    const running = Number(eq.running) || 0;
    const idle = Number(eq.idle) || 0;
    const stopped = Number(eq.stopped) || 0;
    const sum = running + idle + stopped;
    const critical = Number(f.alarms?.error) || 0;
    const env = f.environment || {};
    const rate = num(f.operationRate);
    const share = (n) => (sum > 0 ? `${(n / sum) * 100}%` : "0%");
    const envItem = (label, value, unit) => (<span className="dfv-env__item">
        {label} <b>{value == null ? DASH : formatNumber(value, Number.isInteger(value) ? 0 : 1)}</b>{value != null && unit && <small>{unit}</small>}
      </span>);

    return (<article className="dfv-panel dfv-aerial__card">
      <Illustration code={f.factoryCode} name={f.factoryName}/>

      <div className="dfv-aerial__title">
        <FactoryFlag country={country} cls="dfv-flag" size={20}/>
        <h3>{f.factoryName}</h3>
        {country && <span className="dfv-aerial__en">{COUNTRY_EN[country]}</span>}
        {f.factoryCode && <span className="dfv-code-chip"><i aria-hidden="true"/>{f.factoryCode}</span>}
        <span className={`dfv-badge ${state === "warn" ? "is-warn" : "is-good"}`}>{state === "warn" ? "주의" : "정상"}</span>
      </div>

      <div className="dfv-aerial__body">
        <Donut value={rate} tone={state}/>
        <div className="dfv-aerial__metrics">
          <div className="dfv-aerial__tiles">
            <div className="dfv-tile"><span className="dfv-tile__label">설비 수</span><span className="dfv-tile__value"><b>{formatInt(total)}</b><small>대</small></span></div>
            <div className="dfv-tile" title="마지막 샷 10분 기준 가동 판정"><span className="dfv-tile__label">가동 설비</span><span className="dfv-tile__value"><b>{formatInt(running)}</b><small>대</small></span></div>
            <div className="dfv-tile"><span className="dfv-tile__label">총 알람</span><span className="dfv-tile__value"><b>{formatInt(Number(f.alarms?.total) || 0)}</b><small>건</small></span></div>
          </div>
          <div className="dfv-eqbar">
            <span className="dfv-eqbar__label">설비 상태</span>
            <div className="dfv-eqbar__track" role="img" aria-label={`가동 ${running}, 대기 ${idle}, 정지 ${stopped}`}>
              <i className="is-run" style={{ width: share(running) }}/>
              <i className="is-idle" style={{ width: share(idle) }}/>
              <i className="is-stop" style={{ width: share(stopped) }}/>
            </div>
          </div>
          <div className="dfv-eqbar__legend">
            <span><i className="is-run"/>가동 {running}</span>
            <span><i className="is-idle"/>대기 {idle}</span>
            <span><i className="is-stop"/>정지 {stopped}</span>
          </div>
        </div>
      </div>

      <div className="dfv-aerial__foot">
        <div className="dfv-env" title={env.measuredAt ? `측정 ${env.measuredAt}` : "환경 측정 기록 없음"}>
          {envItem("온도", num(env.temperature), "℃")}
          {envItem("습도", num(env.humidity), "%")}
          {envItem("미세먼지", num(env.pm10), null)}
        </div>
        <div className="dfv-aerial__actions">
          {critical > 0 && (<button type="button" className="dfv-btn dfv-btn--danger" onClick={onCritical} title="미처리 위험 알람 보기">
              Critical {formatInt(critical)}
            </button>)}
          <button type="button" className="dfv-btn dfv-btn--primary" onClick={() => onDetail(f.factoryId)}>
            자세히 보기
            <span className="material-symbols-outlined" aria-hidden="true">chevron_right</span>
          </button>
        </div>
      </div>
    </article>);
});

export function AerialView({ refreshSeq = 0 }) {
    const navigate = useNavigate();
    const { openList } = useAlarmNotification();
    const [factories, setFactories] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const data = await fetchCompanyFactoryOverview();
            setFactories(Array.isArray(data?.factories) ? data.factories : []);
            setError(null);
        }
        catch (err) {
            console.error("공장별 현황 조회 실패:", err);
            setError(err?.message ?? String(err));
        }
    }, []);

    useEffect(() => { load(); }, [load, refreshSeq]);
    useAutoRefresh(load, true);

    const onDetail = useCallback((factoryId) => navigate(`/factory?factoryId=${factoryId}`), [navigate]);

    if (factories == null) {
        return (<div className="dfv-aerial dfv-aerial--msg">
        {error ? <div className="dfv-empty is-bad">공장별 현황을 불러오지 못했습니다: {error}</div> : <div className="dfv-empty">공장별 현황 불러오는 중…</div>}
      </div>);
    }
    if (factories.length === 0) {
        return (<div className="dfv-aerial dfv-aerial--msg"><div className="dfv-empty">표시할 공장이 없습니다.</div></div>);
    }
    const layout = factories.length === 1 ? "one" : factories.length === 2 ? "two" : "many";
    return (<div className={`dfv-aerial dfv-aerial--${layout} dfv-scroll`}>
      {error && <div className="dfv-error-banner dfv-aerial__error" role="alert">최근 갱신 실패 — 마지막 값을 표시 중입니다 ({error})</div>}
      {factories.map((f) => <FactoryCard key={f.factoryId} f={f} onDetail={onDetail} onCritical={openList}/>)}
    </div>);
}
export default AerialView;
